import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowCounterClockwise, Trash, FilePdf, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const hoursLeft = (deletedAt) => {
  if (!deletedAt) return null;
  const left = 24 - (Date.now() - new Date(deletedAt).getTime()) / 36e5;
  return Math.max(0, left);
};

const ITEM_TYPE = {
  report_file: { label: "Report File", icon: FilePdf },
  share_teaser: { label: "Client Teaser", icon: LinkSimple },
  geophys_teaser: { label: "Geo Teaser", icon: LinkSimple },
  geophys_portfolio: { label: "Portfolio Link", icon: LinkSimple },
};

const AutoDeleteBadge = ({ deletedAt }) => {
  const hl = hoursLeft(deletedAt);
  return (
    <Badge variant="outline" className={hl !== null && hl < 4 ? "text-destructive border-destructive/40" : ""}>
      {hl === null ? "-" : hl < 1 ? "< 1 hour" : `~${Math.round(hl)} hours`}
    </Badge>
  );
};

export const TrashView = ({ items, trashItems = [], bytes = 0, onChanged }) => {
  const [emptyOpen, setEmptyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const total = items.length + trashItems.length;

  const restore = async (p) => {
    try {
      await api.post(`/trash/${p.id}/restore`);
      toast.success(`Restored "${p.name}"`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const restoreItem = async (it) => {
    try {
      await api.post(`/trash/items/${it.id}/restore`);
      toast.success(`Restored ${ITEM_TYPE[it.type]?.label || "item"}${it.project_name ? ` for "${it.project_name}"` : ""}`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const emptyTrash = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trash/empty");
      toast.success(`Trash emptied - ${data.purged + (data.purged_items || 0)} item(s) permanently deleted`);
      setEmptyOpen(false);
      onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="trash-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Items in Trash are permanently deleted automatically after <b>24 hours</b>.
          {total > 0 && bytes > 0 && (
            <span data-testid="trash-bytes"> Emptying now frees <b>{fmtBytes(bytes)}</b> of storage.</span>
          )}
        </p>
        {total > 0 && (
          <Button variant="destructive" size="sm" onClick={() => setEmptyOpen(true)} data-testid="trash-empty-btn">
            <Trash size={15} className="mr-1.5" />Empty Trash{bytes > 0 ? ` · ${fmtBytes(bytes)}` : ""}
          </Button>
        )}
      </div>

      {total === 0 ? (
        <Card className="shadow-none p-12 text-center" data-testid="trash-empty-state">
          <h3 className="font-display text-xl font-bold">Trash is empty</h3>
          <p className="text-muted-foreground mt-2 text-sm">Deleted projects, report files and shared links appear here for 24 hours before being removed permanently.</p>
        </Card>
      ) : (
        <>
          {items.length > 0 && (
        <Card className="shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="trash-table">
              <thead>
                <tr className="text-left border-b border-border">
                  <th className="px-4 py-3 overline font-medium">Project Name</th>
                  <th className="px-4 py-3 overline font-medium">Client</th>
                  <th className="px-4 py-3 overline font-medium whitespace-nowrap">Deleted</th>
                  <th className="px-4 py-3 overline font-medium whitespace-nowrap">Auto-delete In</th>
                  <th className="px-4 py-3 w-28"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                    <tr key={p.id} data-testid={`trash-row-${p.id}`} className="border-b border-border/60 hover:bg-accent/40 group">
                      <td className="px-4 py-2.5">
                        <div className="font-semibold">{p.name}</div>
                        {p.country && <div className="text-[11px] text-muted-foreground">{p.country}</div>}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{p.folder_name || p.client_name || "-"}</td>
                      <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap font-mono text-xs">
                        {p.deleted_at ? new Date(p.deleted_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                      </td>
                      <td className="px-4 py-2.5 whitespace-nowrap"><AutoDeleteBadge deletedAt={p.deleted_at} /></td>
                      <td className="px-4 py-2.5">
                        <Button variant="outline" size="sm" onClick={() => restore(p)} data-testid={`trash-restore-${p.id}`}>
                          <ArrowCounterClockwise size={14} className="mr-1.5" />Restore
                        </Button>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
          )}

          {trashItems.length > 0 && (
            <Card className="shadow-none overflow-hidden">
              <div className="px-4 pt-4 overline font-medium">Reports & Shared Links</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="trash-items-table">
                  <thead>
                    <tr className="text-left border-b border-border">
                      <th className="px-4 py-3 overline font-medium">Item</th>
                      <th className="px-4 py-3 overline font-medium">Project</th>
                      <th className="px-4 py-3 overline font-medium whitespace-nowrap">Deleted</th>
                      <th className="px-4 py-3 overline font-medium whitespace-nowrap">Auto-delete In</th>
                      <th className="px-4 py-3 w-28"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {trashItems.map((it) => {
                      const meta = ITEM_TYPE[it.type] || { label: it.type, icon: LinkSimple };
                      const Icon = meta.icon;
                      return (
                        <tr key={it.id} data-testid={`trash-item-row-${it.id}`} className="border-b border-border/60 hover:bg-accent/40">
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <Icon size={16} className="text-muted-foreground shrink-0" />
                              <div>
                                <div className="font-semibold">{it.label}</div>
                                <div className="text-[11px] text-muted-foreground">{meta.label}{it.size ? ` · ${fmtBytes(it.size)}` : ""}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">{it.project_name || "-"}</td>
                          <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap font-mono text-xs">
                            {it.deleted_at ? new Date(it.deleted_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap"><AutoDeleteBadge deletedAt={it.deleted_at} /></td>
                          <td className="px-4 py-2.5">
                            <Button variant="outline" size="sm" onClick={() => restoreItem(it)} data-testid={`trash-item-restore-${it.id}`}>
                              <ArrowCounterClockwise size={14} className="mr-1.5" />Restore
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      <AlertDialog open={emptyOpen} onOpenChange={setEmptyOpen}>
        <AlertDialogContent data-testid="trash-empty-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Empty Trash?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all {total} item{total === 1 ? "" : "s"} in the Trash right now
              {bytes > 0 ? ` and frees ${fmtBytes(bytes)} of storage` : ""},
              including all maps, targets, reports and shared links. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-empty-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={emptyTrash} disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="trash-empty-confirm">
              {busy ? "Deleting…" : "Delete everything permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
